import Button from '@atoms/new/button';
import { Modal, ModalBody, ModalContent, ModalHeader, ModalFooter } from '@atoms/new/modal';
import { Progress, Accordion, AccordionItem } from '@heroui/react';
import { fetchSummary } from '@services/generative-ai';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import { useEffect, useRef, useState, useMemo } from 'react';
import useLoadingMessages from './hooks/loaderMessages';
import { SparkleIcon as AutoAwesomeIcon, WarningCircleIcon as ErrorIcon } from '@phosphor-icons/react';

interface SummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenChange: (isOpen: boolean) => void;
  questionId: string;
  streamId: string;
  type: string;
  isPreview?: boolean;
  previewComments?: any[];
}

const EMPTY_ARRAY: any[] = [];

export const SummaryModal = ({
  isOpen,
  onClose,
  onOpenChange,
  questionId,
  streamId,
  type,
  isPreview = false,
  previewComments = EMPTY_ARRAY
}: SummaryModalProps) => {
  const queryKey = useMemo(() => ['summary', questionId, streamId, type, isPreview, previewComments], [questionId, streamId, type, isPreview, previewComments]);

  const {
    data: summaryData,
    isLoading: isLoadingSummary,
    isPending: isPendingSummary,
    isFetching: isFetchingSummary,
    error: summaryError,
    refetch: refetchSummary
  } = useQuery({
    queryKey,
    queryFn: ({
      queryKey: [, questionId, streamId, type, isPreview, previewComments]
    }) => {
      return fetchSummary({
        questionId: questionId as string,
        streamId: streamId as string,
        type: type as string,
        isPreview: typeof isPreview === 'boolean' ? isPreview : false,
        previewComments: Array.isArray(previewComments) ? previewComments : []
      });
    },
    enabled: !!questionId && !!streamId && !!type && isOpen,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const isLoading = isLoadingSummary || isFetchingSummary || isPendingSummary;
  const isError = !!summaryError || !summaryData?.status;
  const summaryErrorMessage = isError && (summaryError?.message || summaryData?.message);

  const [progressInterval, setProgressInterval] = useState<NodeJS.Timeout | null>(null);

  const [progress, setProgress] = useState(0);
  const [expandedKey, setExpandedKey] = useState<string>(summaryData?.entity?.summary[0]?.heading);

  useEffect(() => {
    if (!isOpen) return;
    if (!isLoading) return;

    const interval = setInterval(() => {
      setProgress((progress) => progress + 20);
    }, 1000);

    setProgressInterval(interval);

    return () => {
      clearInterval(interval);
      setProgressInterval(null);
    };
  }, [isOpen, isLoading]);

  useEffect(() => {
    if (progress < 100) return;
    if (!progressInterval) return;

    clearInterval(progressInterval);
    setProgressInterval(null);
  }, [progress]);

  useEffect(() => {
    if (!isError) return;

    if (!progressInterval) return;

    clearInterval(progressInterval);
    setProgressInterval(null);
  }, [isError]);

  useEffect(() => {
    if (isLoading || !isOpen) return;
    if (summaryData?.entity?.summary?.[0]?.heading !== undefined && expandedKey === undefined) {
      setExpandedKey(summaryData.entity.summary[0].heading);
    }
  }, [summaryData, isLoading, isOpen, expandedKey]);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const isManualClick = useRef(false);
  const clickTimeout = useRef<NodeJS.Timeout>();

  const updateActiveHeading = (container: HTMLDivElement) => {
    if (isManualClick.current) return;

    const containerTop = container.getBoundingClientRect().top;
    let closest = null;
    let minDistance = Infinity;

    summaryData?.entity.summary.forEach((s: any) => {
      const el = document.getElementById(s.heading);
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const dist = Math.abs(rect.top - containerTop);

      if (dist < minDistance) {
        minDistance = dist;
        closest = s.heading;
      }
    });

    if (closest && closest !== expandedKey) {
      setExpandedKey(closest);
    }
  };

  useEffect(() => {
    if (!scrollRef.current) return;

    const container = scrollRef.current;

    const handleScroll = () => {
      updateActiveHeading(container);
    };

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (clickTimeout.current) {
        clearTimeout(clickTimeout.current);
      }
    };
  }, [summaryData]);

  const message = useLoadingMessages(isLoading);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onOpenChange={onOpenChange}
      className='h-[780px]'
      size='xl'
      scrollBehavior='inside'
    >
      <ModalContent>
        <ModalHeader>
          <div className='flex items-center gap-2 mb-2'>
            <AutoAwesomeIcon />
            <p>AI Summary</p>
          </div>
        </ModalHeader>
        <ModalBody
          ref={scrollRef}
        >
          {isError && !isLoading && (
            <div className='p-20 flex flex-col items-center justify-center gap-4 w-full h-full'>
              <ErrorIcon className='dark:text-white text-grey-900' />
              <p className='dark:text-white text-grey-900 text-xl font-normal text-center'>
                {summaryErrorMessage?.includes('No comments found') ?
                  (
                    <>
                      No responses yet. Ask participants to<br />
                      type in the chat and try again.
                    </>
                  ) :
                  'There\'s an issue with summarization feature. Please check your internet connection and try again.'
                }
              </p>

              <Button
                kind='secondary'
                onPress={() => {
                  if (summaryErrorMessage?.includes('No comments found')) {
                    onClose();
                    return;
                  }

                  return refetchSummary();
                }}
              >
                {summaryErrorMessage?.includes('No comments found') ? 'Close' : 'Try again'}
              </Button>
            </div>
          )}

          {isLoading && (
            <div className='p-20 flex flex-col items-center justify-center gap-4 w-full h-full'>
              <Progress
                isStriped
                aria-label="Loading..."
                size="md"
                value={progress}
                classNames={{
                  base: 'color-[#FF3383] w-[356px]'
                }}
              />
              <p className='text-grey-900 dark:text-white text-xl font-normal text-center'>{message}</p>
            </div>
          )}

          {!isError && !isLoading && summaryData && (
            <div className='flex gap-4 w-full'>
              <div className='w-[256px] shrink-0'>
                <div className='flex flex-col gap-y-2 sticky top-0 pr-[16px]'>
                  {summaryData?.entity.summary.map((summary: any) => (
                    <p
                      className={`w-full rounded-md flex items-center px-2 py-1.5 h-8 cursor-pointer ${expandedKey === (summary.heading) ? 'bg-primary-500/20 text-primary-500' : 'text-default-700 hover:bg-content3'}`}
                      onClick={() => {
                        const element = document.getElementById(summary.heading);
                        if (element) {
                          isManualClick.current = true;
                          setExpandedKey(summary.heading);
                          // scrollIntoView targets the nearest scroll container
                          // — ModalBody, which scrolls inside on both web and
                          // embeds — and accounts for the scaled stage
                          // transform. The top offset comes from scroll-mt on
                          // the accordion item.
                          element.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start'
                          });

                          if (clickTimeout.current) {
                            clearTimeout(clickTimeout.current);
                          }
                          clickTimeout.current = setTimeout(() => {
                            isManualClick.current = false;
                          }, 1000);
                        }
                      }}
                    >
                      {summary.heading}
                    </p>
                  ))}
                </div>
              </div>
              <div className='flex flex-col gap-y-4 flex-1 min-w-0'>
                <Accordion
                  selectedKeys={'all'}
                  onSelectionChange={(keys) => { }}
                  selectionMode='single'
                  variant='splitted'
                  className='!p-0 !m-0 !w-full'
                >
                  {summaryData?.entity.summary.map((summary: any) => (
                    <AccordionItem
                      id={summary.heading}
                      classNames={{
                        base: 'dark:bg-grey-800 bg-grey-50 p-0 rounded-lg shadow-none border border-grey-200 dark:border-grey-700 scroll-mt-[200px]',
                        title: 'text-grey-900 dark:text-white font-semibold text-[20px]',
                        trigger: 'px-4 py-2',
                        indicator: 'text-grey-900 dark:text-white',
                        content: 'p-4 text-grey-900 dark:text-white border-t border-grey-200 dark:border-grey-700',
                      }}
                      key={summary.heading}
                      title={summary.heading}
                      hideIndicator={true}
                    >
                      <ReactMarkdown className='flex flex-col gap-4 text-[20px] leading-12'>{summary.content}</ReactMarkdown>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>

          )}
        </ModalBody>
        <ModalFooter>
          {(!isLoading && !isError) &&
            <div className="flex items-center w-full justify-end">
              <Button onPress={onClose}>Got it</Button>
            </div>}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
